import { HttpTypes } from "@medusajs/types"
import { keepPreviousData } from "@tanstack/react-query"
import { TFunction } from "i18next"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useApiKeys,
  useCollections,
  useCustomerGroups,
  useCustomers,
  useOrders,
  useProductCategories,
  useProducts,
  useProductTags,
  useProductTypes,
  useUsers,
  useVariants,
} from "../../hooks/api"
import { Shortcut, ShortcutType } from "../../providers/keybind-provider/types"
import { useGlobalShortcuts } from "../../providers/keybind-provider/hooks"
import { SearchArea } from "./types"

export type SearchResultGroup = {
  title: string
  items: SearchResultItem[]
}

export type SearchResultItem = {
  title: string
  subtitle?: string
  to: string
}

const DEFAULT_SEARCH_LIMIT = 5

export const useSearchResults = ({ 
  area, 
  limit, 
  q: query 
}: { 
  area: SearchArea
  limit: number
  q: string 
}) => {
  const { t } = useTranslation()
  const [debouncedSearch, setDebouncedSearch] = useState("")

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(query)
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [query])

  const shouldSearch = debouncedSearch.length > 0

  const searchLimit = limit || DEFAULT_SEARCH_LIMIT
  const shouldSearchProducts = shouldSearch && (area === "all" || area === "product")
  const shouldSearchCollections = shouldSearch && (area === "all" || area === "collection")
  const shouldSearchCustomers = shouldSearch && (area === "all" || area === "customer")
  const shouldSearchOrders = shouldSearch && (area === "all" || area === "order")
  const shouldSearchUsers = shouldSearch && (area === "all" || area === "user")

  // Essential API calls for simplified commerce
  const productResponse = useProducts(
    {
      q: debouncedSearch,
      limit: searchLimit,
      fields: "id,title,handle,thumbnail",
    },
    {
      enabled: shouldSearchProducts,
      placeholderData: keepPreviousData,
    }
  )

  const collectionResponse = useCollections(
    {
      q: debouncedSearch,
      limit: searchLimit,
      fields: "id,title,handle",
    },
    {
      enabled: shouldSearchCollections,
      placeholderData: keepPreviousData,
    }
  )

  const customerResponse = useCustomers(
    {
      q: debouncedSearch,
      limit: searchLimit,
      fields: "id,email,first_name,last_name",
    },
    {
      enabled: shouldSearchCustomers,
      placeholderData: keepPreviousData,
    }
  )

  const orderResponse = useOrders(
    {
      q: debouncedSearch,
      limit: searchLimit,
      fields: "id,display_id,email,status",
    },
    {
      enabled: shouldSearchOrders,
      placeholderData: keepPreviousData,
    }
  )

  const userResponse = useUsers(
    {
      q: debouncedSearch,
      limit: searchLimit,
      fields: "id,email,first_name,last_name",
    },
    {
      enabled: shouldSearchUsers,
      placeholderData: keepPreviousData,
    }
  )

  const results = useMemo(() => {
    if (!shouldSearch) {
      return []
    }

    const groups: any[] = []

    // Products
    if (productResponse.products?.length) {
      groups.push({
        area: "product" as SearchArea,
        title: t("products.domain"),
        hasMore: false, // TODO: Implement pagination logic
        count: productResponse.products.length,
        items: productResponse.products.map((product) => ({
          id: product.id,
          title: product.title,
          subtitle: product.handle,
          to: `/products/${product.id}`,
          thumbnail: product.thumbnail,
          value: `product-${product.id}`,
        })),
      })
    }

    // Collections
    if (collectionResponse.collections?.length) {
      groups.push({
        area: "collection" as SearchArea,
        title: t("collections.domain"),
        hasMore: false,
        count: collectionResponse.collections.length,
        items: collectionResponse.collections.map((collection) => ({
          id: collection.id,
          title: collection.title,
          subtitle: collection.handle,
          to: `/collections/${collection.id}`,
          value: `collection-${collection.id}`,
        })),
      })
    }

    // Customers
    if (customerResponse.customers?.length) {
      groups.push({
        area: "customer" as SearchArea,
        title: t("customers.domain"),
        hasMore: false,
        count: customerResponse.customers.length,
        items: customerResponse.customers.map((customer) => ({
          id: customer.id,
          title: `${customer.first_name} ${customer.last_name}`.trim() || customer.email,
          subtitle: customer.email,
          to: `/customers/${customer.id}`,
          value: `customer-${customer.id}`,
        })),
      })
    }

    // Orders
    if (orderResponse.orders?.length) {
      groups.push({
        area: "order" as SearchArea,
        title: t("orders.domain"),
        hasMore: false,
        count: orderResponse.orders.length,
        items: orderResponse.orders.map((order) => ({
          id: order.id,
          title: `#${order.display_id}`,
          subtitle: order.email,
          to: `/orders/${order.id}`,
          value: `order-${order.id}`,
        })),
      })
    }

    // Users
    if (userResponse.users?.length) {
      groups.push({
        area: "user" as SearchArea,
        title: t("users.domain"),
        hasMore: false,
        count: userResponse.users.length,
        items: userResponse.users.map((user) => ({
          id: user.id,
          title: `${user.first_name} ${user.last_name}`.trim() || user.email,
          subtitle: user.email,
          to: `/settings/users/${user.id}`,
          value: `user-${user.id}`,
        })),
      })
    }

    return groups
  }, [
    shouldSearch,
    productResponse.products,
    collectionResponse.collections,
    customerResponse.customers,
    orderResponse.orders,
    userResponse.users,
    t,
  ])

  const isLoading = 
    productResponse.isPending ||
    collectionResponse.isPending ||
    customerResponse.isPending ||
    orderResponse.isPending ||
    userResponse.isPending

  return {
    staticResults: [], // Static results for shortcuts/navigation - empty for now
    dynamicResults: results,
    isFetching: isLoading,
  }
}

export const useSearchShortcuts = () => {
  const { registerShortcuts } = useGlobalShortcuts()

  const shortcuts = useMemo<Shortcut[]>(() => {
    return [
      {
        label: "Search",
        keys: { Mac: ["cmd", "k"], Windows: ["ctrl", "k"], Linux: ["ctrl", "k"] },
        type: "commandShortcut",
        callback: () => {
          // This will be handled by the search component
        },
      },
    ]
  }, [])

  useEffect(() => {
    registerShortcuts(shortcuts)
  }, [registerShortcuts, shortcuts])
}